// components/MobileAppLayout.tsx
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppNavigation, AppView } from '../hooks/useAppNavigation';
import { MobileHeader } from './MobileHeader';
import { MobileBottomNav } from './MobileBottomNav';
import { HeroSection } from '../sections/HeroSection';
import { NewSpotifySection } from '../sections/NewSpotifySection';
import { NewCanvasSection } from '../sections/NewCanvasSection';
import { CalendarioView } from '../sections/CalendarSection';
import Silk from '../blocks/Backgrounds/Silk/Silk';
import { MobileMapsSection } from '../sections/MobileMapsSection';



// Wrapper para las secciones existentes para que ocupen toda la pantalla
const FullScreenWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="flex-1 overflow-auto">
    {children}
  </div>
);

interface MobileAppLayoutProps {
  currentView: AppView;
  onNavigate: (view: AppView) => void;
  // Props para el usuario (necesarios para el canvas)
  userId?: string;
  userName?: string;
}

export const MobileAppLayout = ({
  currentView,
  onNavigate,
  userId = 'user-1', // Default fallback
  userName = 'Usuario' // Default fallback
}: MobileAppLayoutProps) => {
  const [isLandscape, setIsLandscape] = useState(false);

  // Detect orientation changes
  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // Hide nav bars when canvas is in landscape mode
  const hideNavBars = currentView === 'canvas' && isLandscape;

  const renderView = () => {
    switch (currentView) {
      case 'hero':
        return (
          <FullScreenWrapper>
            <HeroSection />
          </FullScreenWrapper>
        );
      case 'canvas':
        return (
          <FullScreenWrapper>
            <NewCanvasSection userId={userId} userName={userName} />
          </FullScreenWrapper>
        );
      case 'calendario':
        return (
          <FullScreenWrapper>
            <div className="h-full">
              <CalendarioView userId={userId} userName={userName} />
            </div>
          </FullScreenWrapper>
        );
      case 'maps':
        return (
          <FullScreenWrapper>
            <MobileMapsSection />
          </FullScreenWrapper>
        );
      case 'spotify':
        return (
          <FullScreenWrapper>
            <NewSpotifySection />
          </FullScreenWrapper>
        );
      default:
        return (
          <FullScreenWrapper>
            <HeroSection />
          </FullScreenWrapper>
        );
    }
  };

  // Animaciones para transiciones entre vistas
  const pageVariants = {
    initial: { opacity: 0, x: 20 },
    in: { opacity: 1, x: 0 },
    out: { opacity: 0, x: -20 }
  };

  const pageTransition = {
    type: 'spring' as const,
    damping: 25,
    stiffness: 300
  };

  return (
    <div className="h-screen flex flex-col relative">
      {/* Fondo Silk */}
      {!hideNavBars && (
        <div className="fixed inset-0 z-0">
          <Silk
            speed={5}
            scale={1}
            color="#0066ff"
            noiseIntensity={1.0}
            rotation={0}
          />
        </div>
      )}

      {/* Header fijo - Hidden in landscape canvas */}
      {!hideNavBars && <MobileHeader currentView={currentView} />}

      {/* Contenido principal con transiciones */}
      <AnimatePresence mode="wait">
        <motion.main
          key={currentView}
          initial="initial"
          animate="in"
          exit="out"
          variants={pageVariants}
          transition={pageTransition}
          className={hideNavBars ? "flex-1 overflow-auto" : "flex-1 overflow-auto pt-16 pb-20"}
        >
          {renderView()}
        </motion.main>
      </AnimatePresence>

      {/* Bottom navigation fija - Hidden in landscape canvas */}
      {!hideNavBars && <MobileBottomNav currentView={currentView} onNavigate={onNavigate} />}
    </div>
  );
};